import { Fragment, memo } from 'react';
import { useNavigate } from 'react-router-dom';

import { Skeleton } from '@/shared/components/skeleton';
import { useScrollShadow } from '@/shared/hooks/use-scroll-shadow';

import {
  StyledDataCell,
  StyledEmptyState,
  StyledExpandableRowGroup,
  StyledRowSurface,
  StyledScrollEndShadow,
  StyledTableBody,
  StyledTableDataRow,
  StyledTableRoot,
  StyledTableRow,
  StyledTableWrapper,
} from './Table.styled';
import { buildGridTemplate, getPinSide, needsFillerTrack } from './Table.utils';
import { TableHeaderCell } from './TableHeaderCell';
import type { TableProps, TableRow } from './types';

const SKELETON_WIDTHS = ['55%', '70%', '45%', '65%', '50%'];

function TableComponent<T extends TableRow = TableRow>({
  columns,
  rows,
  size = 'md',
  variant = 'default',
  sortKey,
  sortDirection,
  onSort,
  className,
  isLoading = false,
  emptyState,
  skeletonRowCount = 5,
  minTableWidth,
  maxHeight,
  stickyHeader = false,
  roundedScrollEdges = true,
  renderExpandedRow,
}: TableProps<T>) {
  const gridTemplateColumns = buildGridTemplate(columns);
  // With a filler track in play the last column has to be placed explicitly,
  // otherwise auto-placement drops it into the filler and it stops short of the
  // right edge.
  const anchorLastColumn = needsFillerTrack(columns);
  const lastColumnKey = columns[columns.length - 1]?.key;

  const navigate = useNavigate();
  const { ref: scrollRef, showEndShadow } = useScrollShadow<HTMLDivElement>();

  const rowSurface = roundedScrollEdges ? (
    <StyledRowSurface $variant={variant} data-row-surface aria-hidden />
  ) : null;

  const rowMinWidthStyle = minTableWidth
    ? { minWidth: typeof minTableWidth === 'number' ? `${minTableWidth}px` : minTableWidth }
    : undefined;

  return (
    <StyledTableWrapper className={className}>
      <StyledTableRoot ref={scrollRef} $containQueries={roundedScrollEdges} $maxHeight={maxHeight}>
        {/* Header */}
        <StyledTableRow
          $gridTemplateColumns={gridTemplateColumns}
          $sticky={stickyHeader}
          style={rowMinWidthStyle}
          role="row"
        >
          {columns.map((column) => (
            <TableHeaderCell
              key={column.key}
              label={column.header}
              tooltip={column.headerTooltip}
              align={column.align}
              sortable={column.sortable}
              sortDirection={sortKey === column.key ? (sortDirection ?? null) : null}
              onClick={column.sortable && onSort ? () => onSort(column.key) : undefined}
              pinned={column.pinned}
              anchorEnd={anchorLastColumn && column.key === lastColumnKey}
            />
          ))}
        </StyledTableRow>

        {/* Body */}
        <StyledTableBody role="rowgroup" $variant={variant}>
          {isLoading ? (
            Array.from({ length: skeletonRowCount }, (_, rowIndex) => (
              <StyledTableDataRow
                key={rowIndex}
                $gridTemplateColumns={gridTemplateColumns}
                style={rowMinWidthStyle}
                role="row"
                $roundedScrollEdges={roundedScrollEdges}
              >
                {rowSurface}
                {columns.map((column, colIndex) => (
                  <StyledDataCell
                    key={column.key}
                    $align={column.align}
                    $size={size}
                    $pin={getPinSide(column.pinned)}
                    $anchorEnd={anchorLastColumn && column.key === lastColumnKey}
                    $columnStart={roundedScrollEdges ? colIndex + 1 : undefined}
                    role="cell"
                  >
                    <Skeleton
                      width={SKELETON_WIDTHS[(rowIndex + colIndex) % SKELETON_WIDTHS.length]}
                    />
                  </StyledDataCell>
                ))}
              </StyledTableDataRow>
            ))
          ) : !rows.length && emptyState ? (
            <StyledEmptyState role="row">{emptyState}</StyledEmptyState>
          ) : (
            rows.map((row, rowIndex) => {
              const expandedContent = renderExpandedRow?.(row, rowIndex);
              const activate = row?.onClick
                ? row.onClick
                : row?.href
                  ? () => navigate(row.href as string)
                  : undefined;
              const dataRow = (
                <StyledTableDataRow
                  $gridTemplateColumns={gridTemplateColumns}
                  style={rowMinWidthStyle}
                  role="row"
                  data-changed={row.changed ? 'true' : undefined}
                  data-alert={row.alert ? 'true' : undefined}
                  data-highlight={row.highlight ? 'true' : undefined}
                  tabIndex={activate ? 0 : undefined}
                  onClick={activate}
                  onKeyDown={
                    activate &&
                    ((event) => {
                      if (event.target !== event.currentTarget) return;
                      if (event.key !== 'Enter' && event.key !== ' ') return;

                      event.preventDefault();
                      activate();
                    })
                  }
                  $isLink={!!row.href || !!row.onClick}
                  $variant={variant}
                  $roundedScrollEdges={roundedScrollEdges}
                  data-row-key={row.key}
                >
                  {rowSurface}
                  {columns.map((column, colIndex) => (
                    <StyledDataCell
                      key={column.key}
                      $align={column.align}
                      $size={size}
                      $pin={getPinSide(column.pinned)}
                      $anchorEnd={anchorLastColumn && column.key === lastColumnKey}
                      $columnStart={roundedScrollEdges ? colIndex + 1 : undefined}
                      data-pinned={getPinSide(column.pinned)}
                      role="cell"
                    >
                      {column.cell(row, rowIndex)}
                    </StyledDataCell>
                  ))}
                </StyledTableDataRow>
              );

              return expandedContent ? (
                <StyledExpandableRowGroup key={row.key} $variant={variant} style={rowMinWidthStyle}>
                  {dataRow}
                  {expandedContent}
                </StyledExpandableRowGroup>
              ) : (
                <Fragment key={row.key}>{dataRow}</Fragment>
              );
            })
          )}
        </StyledTableBody>
      </StyledTableRoot>
      <StyledScrollEndShadow $visible={showEndShadow} />
    </StyledTableWrapper>
  );
}

TableComponent.displayName = 'Table';

const Table = memo(TableComponent) as typeof TableComponent;

export { Table };
